import { faGlobe } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { queryString } from '@olivierpascal/helpers';
import { Button, Stack, Text } from '@sixui/ui';
import stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';

import type { ITableRowProps } from '~/components/molecules/Table';
import type { IMaybeAsync } from '~/helpers/types';
import { UserAvatar } from '~/components/atoms/UserAvatar';
import { Table } from '~/components/molecules/Table';
import { parseUserInfo } from '~/helpers/parseUserInfo';
import { appRegionsSettings } from '~/lib/appSettings';
import { IAuthUser, useAuthUser } from '~/lib/auth';
import { IAuthCacheEntry } from '~/lib/auth/cache';

export type ISessionProps = ITableRowProps & {
  credentials: IAuthCacheEntry<IAuthUser>;
  nextUrl?: string;
  onClose?: () => IMaybeAsync<void>;
};

const styles = stylex.create({
  me: {
    fontWeight: 'bold',
  },
});

export const Session: React.FC<ISessionProps> = (props) => {
  const { credentials, nextUrl, onClose, ...other } = props;
  const userInfo = parseUserInfo(credentials.user);
  const { t } = useTranslation();
  const { fullName, emailAddress } = userInfo;
  const authUser = useAuthUser();
  const isCurrent = authUser?._id === credentials.user._id;
  const appRegion = credentials.region;
  const appRegionName = appRegion
    ? appRegionsSettings[appRegion].name
    : undefined;

  return (
    <Table.Row {...other}>
      <Stack
        as={Table.BodyAction}
        horizontal
        gap={3}
        href={`^/sessions/switch/${credentials.sessionId}?${queryString.build({
          nextUrl,
        })}`}
        sx={isCurrent && styles.me}
      >
        <UserAvatar
          user={credentials.user}
          isSuperAdmin={credentials.user.superAdmin}
        />
        <Stack gap={1}>
          <Text variant="title" size="sm" truncate>
            {fullName ?? credentials.user._id}
            {isCurrent ? ` (${t('common.current')})` : null}
          </Text>
          <Text variant="body" size="sm" dimmed>
            {emailAddress}
          </Text>
        </Stack>
      </Stack>
      <Stack as={Table.Body} horizontal gap={2}>
        <FontAwesomeIcon icon={faGlobe} />
        <Text truncate>{appRegionName}</Text>
      </Stack>
      <Stack as={Table.Body} horizontal justify="end">
        <Button variant="text" onClick={onClose}>
          {t('Session.closeSession')}
        </Button>
      </Stack>
    </Table.Row>
  );
};
